import User from "../models/User_b";

class AdminController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    normalizeListResponse(raw) {
        // APIService.get returns axios `response.data`
        // Some endpoints return { data: [...], meta: {...} }
        // Others return apiSuccess: { data: { data: [...], meta: {...} }, message, statusCode }
        const dataContainer = raw?.data?.data !== undefined ? raw.data : raw;
        const list = dataContainer?.data ?? dataContainer;
        const meta = dataContainer?.meta ?? raw?.meta;
        return {
            list: Array.isArray(list) ? list : [],
            meta: meta || {},
        };
    }

    async getUsers(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.skip) queryParams.append('skip', params.skip);
        if (params.take) queryParams.append('take', params.take);
        if (params.role) queryParams.append('role', params.role);
        if (params.status) queryParams.append('status', params.status);
        
        const queryString = queryParams.toString();
        const url = `/admin/users${queryString ? `?${queryString}` : ''}`;
        
        return this.apiService.get(url).then((raw) => {
            const { list, meta } = this.normalizeListResponse(raw);
            return {
                data: list.map((user) => User.fromData(user)),
                meta,
            };
        });
    }

    async getUserStats() {
        return this.apiService.get('/admin/users/stats').then((raw) => raw?.data ?? raw);
    }

    async getFigureDraftStats() {
        return this.apiService.get('/admin/figures/drafts/stats/overview').then((res) => res?.data ?? res);
    }

    async getFigures(params = {}) {
        return this.apiService.get('/admin/figures', { params }).then((raw) => {
            const payload = raw?.data ?? raw;
            const container = payload?.data?.data !== undefined ? payload.data : payload;
            const list = container?.data ?? [];
            const meta = container?.meta ?? {};
            return { data: Array.isArray(list) ? list : [], meta };
        });
    }

    async getFigureStats() {
        return this.apiService.get('/admin/figures/stats').then((raw) => raw?.data ?? raw);
    }

    async getPendingCompanyDrafts(params = { skip: 0, take: 1 }) {
        return this.apiService.get('/admin/company/drafts/pending', { params }).then((raw) => {
            const { list, meta } = this.normalizeListResponse(raw);
            return { data: list, meta };
        });
    }

    async searchUsers(query, skip = 0, take = 10) {
        const queryParams = new URLSearchParams();
        queryParams.append('q', query);
        queryParams.append('skip', skip);
        queryParams.append('take', take);
        
        return this.apiService
            .get(`/admin/users/search?${queryParams.toString()}`)
            .then((raw) => {
                const { list, meta } = this.normalizeListResponse(raw);
                return {
                    data: list.map((user) => User.fromData(user)),
                    meta,
                };
            });
    }

    async getUserById(id) {
        return this.apiService.get(`/admin/users/${id}`)
            .then(user => User.fromData(user));
    }

    async createUser(userData) {
        return this.apiService.post('/admin/users', userData)
            .then(user => User.fromData(user));
    }

    async updateUser(id, userData) {
        return this.apiService.patch(`/admin/users/${id}`, userData)
            .then(user => User.fromData(user));
    }

    async deleteUser(id) {
        return this.apiService.delete(`/admin/users/${id}`);
    }

    async hardDeleteUser(id) {
        return this.apiService.delete(`/admin/users/${id}/hard-delete`);
    }

    async suspendUser(id) {
        return this.apiService.patch(`/admin/users/${id}/suspend`);
    }

    async activateUser(id) {
        return this.apiService.patch(`/admin/users/${id}/activate`);
    }

    async setTrustScore(userId, score) {
        return this.apiService.patch(`/admin/users/${userId}/trust-score`, { score })
            .then((raw) => raw?.data ?? raw);
    }

    async activatePremium(userId, expiresAt) {
        const body = expiresAt ? { expiresAt } : {};
        return this.apiService.patch(`/admin/users/${userId}/premium/activate`, body)
            .then((raw) => raw?.data ?? raw);
    }

    async deactivatePremium(userId) {
        return this.apiService.patch(`/admin/users/${userId}/premium/deactivate`, {})
            .then((raw) => raw?.data ?? raw);
    }

    // Admin Management
    async createAdmin(adminData) {
        return this.apiService.post('/admin/admins', adminData)
            .then(admin => User.fromObject(admin));
    }

    async getAllAdmins() {
        return this.apiService.get('/admin/admins').then((raw) => {
            const list = raw?.data?.data ?? raw?.data ?? raw;
            return (Array.isArray(list) ? list : []).map((admin) => User.fromObject(admin));
        });
    }

    async updateAdminLevel(id, updateData) {
        return this.apiService.patch(`/admin/admins/${id}`, updateData)
            .then(admin => User.fromObject(admin));
    }

    // Moderator Management
    async createModerator(moderatorData) {
        return this.apiService.post('/admin/moderators', moderatorData)
            .then(moderator => User.fromObject(moderator));
    }

    async getAllModerators() {
        return this.apiService.get('/admin/moderators').then((raw) => {
            const list = raw?.data?.data ?? raw?.data ?? raw;
            return (Array.isArray(list) ? list : []).map((moderator) => User.fromObject(moderator));
        });
    }

    async updateModeratorPosition(id, updateData) {
        return this.apiService.patch(`/admin/moderators/${id}`, updateData)
            .then(moderator => User.fromObject(moderator));
    }
}

export default AdminController;